import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import MainDialogLayout from '@/components/ui/MainDialogLayout/MainDialogLayout';
import Button from '@/components/ui/Button';
import { SupportRequestDtoIn } from '@/models/SupportRequestDtoIn';
import { MoveToNextMonthItemResultDtoIn } from '@/models/in/MoveToNextMonthItemResultDtoIn';
import { MoveSupportRequestsToNextMonthDtoOut } from '@/models/out/MoveSupportRequestsToNextMonthDtoOut';
import SupportRequestType from '@/models/common/SupportRequestType';
import AllowedFor from '@/models/common/AllowedFor';
import { getAggregatedStatus } from '@/helpers/requestStatus';
import { usePutAuthApi } from '@/hooks/UseApiCalls';
import useFundContext from '@/contexts/Fund/useFundContext';
import styles from './MoveToNextMonthDialog.module.css';

interface MoveToNextMonthDialogProps {
  open: boolean;
  onClose: () => void;
  /** The rejected requests to move. A single-request move passes an array of one. */
  requests: SupportRequestDtoIn[];
  onSuccess: () => void;
}

/** True when the date's eligibility month is the previous calendar month. */
const isPreviousMonth = (isoDate: string): boolean => {
  const date = new Date(isoDate);
  const now = new Date();
  const previousMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return date.getFullYear() === previousMonth.getFullYear() && date.getMonth() === previousMonth.getMonth();
};

/**
 * The move-to-next-month action is limited to rejected one-time beneficiary requests from the
 * previous calendar month (so the move always lands in the current month - reviving older
 * rejections wholesale is not the feature's intent). Monthly requests would collide with their
 * own following month, and fund-expense requests have no per-month eligibility to re-check.
 */
export const canMoveToNextMonth = (request: SupportRequestDtoIn): boolean =>
  request.type === SupportRequestType.OneTime &&
  request.goal?.allowedFor !== AllowedFor.Fund &&
  getAggregatedStatus(request.statuses) === 'Rejected' &&
  isPreviousMonth(request.requestedForDates[0]);

/** MM/YYYY label of the month following the request's current eligibility date. */
const getNextMonthLabel = (isoDate: string): string => {
  const current = new Date(isoDate);
  // Build from year+month only, so a day like the 31st can't overflow into the month after next.
  const next = new Date(current.getFullYear(), current.getMonth() + 1, 1);
  return `${String(next.getMonth() + 1).padStart(2, '0')}/${next.getFullYear()}`;
};

const MoveToNextMonthDialog = ({ open, onClose, requests, onSuccess }: MoveToNextMonthDialogProps) => {
  const { t } = useTranslation();
  const { currentFund } = useFundContext();

  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<MoveToNextMonthItemResultDtoIn[] | null>(null);

  const { mutate: moveToNextMonth, isPending: isSubmitting } = usePutAuthApi<MoveToNextMonthItemResultDtoIn[], object, MoveSupportRequestsToNextMonthDtoOut>(
    `funds/${currentFund?.id}/support-requests/move-to-next-month`
  );

  useEffect(() => {
    if (open) {
      setComment('');
      setError(null);
      setResults(null);
    }
  }, [open]);

  const handleSubmit = () => {
    if (!currentFund?.id || requests.length === 0) return;
    setError(null);

    const body: MoveSupportRequestsToNextMonthDtoOut = {
      correlationIds: requests.map((r) => r.correlationId),
      comment: comment.trim() || undefined,
    };

    moveToNextMonth(
      { body },
      {
        onSuccess: (moveResults) => {
          setResults(moveResults);
          onSuccess();
        },
        onError: (err) => {
          setError(t('moveToNextMonth.error') + ': ' + err.message);
        },
      }
    );
  };

  if (requests.length === 0) return null;

  const requestName = (request: SupportRequestDtoIn) =>
    request.supportedContact ? `${request.supportedContact.firstName} ${request.supportedContact.lastName}` : '';

  const renderConfirmation = () => (
    <>
      <p className={styles.confirmText}>
        {requests.length === 1
          ? t('moveToNextMonth.confirmSingle', { month: getNextMonthLabel(requests[0].requestedForDates[0]) })
          : t('moveToNextMonth.confirmMultiple', { count: requests.length })}
      </p>

      {requests.length > 1 && (
        <div className={styles.requestList}>
          {requests.map((request) => (
            <div key={request.correlationId} className={styles.requestRow}>
              <span>{requestName(request)}</span>
              <span>{request.amount.toLocaleString('he-IL', { style: 'currency', currency: 'ILS' })}</span>
              <span>{getNextMonthLabel(request.requestedForDates[0])}</span>
            </div>
          ))}
        </div>
      )}

      <div className={styles.section}>
        <label className={styles.sectionLabel}>{t('moveToNextMonth.commentLabel')}:</label>
        <textarea
          className={styles.commentTextarea}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder={t('moveToNextMonth.commentPlaceholder')}
          rows={3}
        />
      </div>

      {error && <p className={styles.error}>{error}</p>}

      <div className={styles.actions}>
        <Button variant="white" className={styles.secondaryButton} onClick={onClose} disabled={isSubmitting}>
          {t('moveToNextMonth.cancel')}
        </Button>
        <Button variant="primary" className={styles.actionButton} onClick={handleSubmit} disabled={isSubmitting}>
          {isSubmitting ? t('loading') : t('moveToNextMonth.confirm')}
        </Button>
      </div>
    </>
  );

  const renderResults = (moveResults: MoveToNextMonthItemResultDtoIn[]) => {
    const movedToExceptions = moveResults.filter((r) => r.movedToExceptionsCommittee);
    const returnedToPending = moveResults.filter((r) => !r.movedToExceptionsCommittee);

    const isSingle = moveResults.length === 1;

    return (
      <div className={styles.successContent}>
        {returnedToPending.length > 0 && (
          <p className={styles.successMessage}>
            {isSingle ? t('moveToNextMonth.resultSingleReturnedToPending') : t('moveToNextMonth.resultReturnedToPending', { count: returnedToPending.length })}
          </p>
        )}
        {movedToExceptions.length > 0 && (
          <p className={styles.exceptionsMessage}>
            {isSingle ? t('moveToNextMonth.resultSingleMovedToExceptions') : t('moveToNextMonth.resultMovedToExceptions', { count: movedToExceptions.length })}
          </p>
        )}

        {moveResults.length > 1 && (
          <div className={styles.requestList}>
            {moveResults.map((result) => {
              const request = requests.find((r) => r.correlationId === result.correlationId);
              return (
                <div key={result.correlationId} className={styles.requestRow}>
                  <span>{request ? requestName(request) : ''}</span>
                  <span>{result.movedToExceptionsCommittee ? t('moveToNextMonth.itemMovedToExceptions') : t('moveToNextMonth.itemReturnedToPending')}</span>
                </div>
              );
            })}
          </div>
        )}

        <div className={styles.actions}>
          <Button variant="primary" className={styles.actionButton} onClick={onClose}>
            {t('moveToNextMonth.close')}
          </Button>
        </div>
      </div>
    );
  };

  return (
    <MainDialogLayout
      open={open}
      onOpenChange={(isOpen: boolean) => {
        if (!isOpen) onClose();
      }}
      title={t('moveToNextMonth.title')}
      contentClassName={styles.dialogBox}
    >
      <div className={styles.content}>{results ? renderResults(results) : renderConfirmation()}</div>
    </MainDialogLayout>
  );
};

export default MoveToNextMonthDialog;
